import React, { useState, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBoxArchive, faCirclePause, faLock } from '@fortawesome/free-solid-svg-icons';

import { PrimaryButton, SecondaryButton } from '../../components/button/Button';
import { Select } from '@/components/form/Select.jsx';
import { UserAuth } from '../../authentication/AuthContext.jsx';
import { updateDashboardContext, setEffectivePermissions } from '../../redux-store/ReduxStore.js';
import { updateActiveContext } from '../../services/UserProfileService.jsx';
import { getOrgById } from '../../services/admin-settings/org-management/OrgRegistrationService';
import { getUserGameRegistries } from '../../services/admin-settings/UserGameService';
import { getGameRegistriesByOrgIdWithAccessibility } from '../../services/admin-settings/GameService';
import { isSuspendedMembershipLockout, suspendedScopeDetails } from '../../utils/membershipAccess';
import { LANDING_PATH } from '../../config/accessControl.js';

import './errorPages.css';

// Org-membership states in which the user is blocked everywhere — used to show a
// suspension-specific message instead of the generic "not authorized / please log in".
const DENYING_STATES = ['SUSPENDED', 'ARCHIVED'];

// Closing advice, per lockout scope and per state. Suspension is reversible, so it promises a
// restore; archive is terminal (OrgRegistryStateRegistry marks it so, and the org roster offers no
// Activate on an archived member), so its copy points at being granted access again instead.
const ADVICE = {
    org: {
        SUSPENDED: 'Please contact your organization administrator to restore access.',
        ARCHIVED: 'Your organization administrator will need to re-invite you to regain access.',
    },
    singleBuild: {
        SUSPENDED: 'This is your only access right now, so you can’t open the dashboard. Once it’s restored — or you’re granted access to another game or build — you’ll be able to sign back in. Contact your game owner or organization administrator to restore access.',
        ARCHIVED: 'This was your only access, so you can’t open the dashboard. Once you’re granted access to another game or build, you’ll be able to sign back in. Contact your game owner or organization administrator to grant it again.',
    },
    scopes: {
        SUSPENDED: 'You have no other active access right now, so you can’t open the dashboard. Once access is restored — or you’re granted another game or build — you’ll be able to sign back in. Contact your game owner or organization administrator to restore access.',
        ARCHIVED: 'You have no other active access right now, so you can’t open the dashboard. Once you’re granted another game or build, you’ll be able to sign back in. Contact your game owner or organization administrator to grant it again.',
    },
};

const NotAuthorized = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { user, logOut } = UserAuth();

    const orgs = useSelector(s => s.userDetails?.orgs) || [];
    const selectedOrgId = useSelector(s => s.dashboardContext?.selectedOrgId);

    const currentOrg = orgs.find(o => o.orgId === selectedOrgId);
    // Suspended/archived at the ORG level — in the current org (or, if none is selected yet, in any
    // org the user belongs to). They're logged in, so "please log in" would be misleading. Keep the
    // STATE and not just a flag: suspend and archive are different messages (see lockState below).
    const orgDenyingState = (currentOrg
        ? (DENYING_STATES.includes(currentOrg.orgState) ? currentOrg.orgState : null)
        : orgs.find(o => DENYING_STATES.includes(o.orgState))?.orgState) || null;
    const orgSuspended = !!orgDenyingState;

    // Build/game-level suspension: the user keeps active ORG membership but their only game/build
    // access has been suspended at the client level (e.g. a Developer suspended from their one
    // build via Game User Management). orgState is NOT denying here, so without this check they'd
    // fall through to the generic "Not Authorized" copy. We fetch the registries to detect it.
    const [registries, setRegistries] = useState(null); // null = not yet loaded
    useEffect(() => {
        if (orgSuspended || !user?.accessToken) return; // org-level case needs no registry fetch
        let cancelled = false;
        getUserGameRegistries({ accessToken: user.accessToken })
            .then(data => { if (!cancelled) setRegistries(data?.userGameRegistryDTOS || []); })
            .catch(err => {
                console.error('NotAuthorized: failed to load game registries', err);
                if (!cancelled) setRegistries([]);
            });
        return () => { cancelled = true; };
    }, [orgSuspended, user?.accessToken]);

    const membershipSuspended = !orgSuspended && registries !== null
        && isSuspendedMembershipLockout(registries);
    const suspended = orgSuspended || membershipSuspended;

    // While we don't yet know whether a non-org-suspended lockout is a suspension vs. a plain
    // permission gap, hold the render so we don't flash the generic page then swap to suspended.
    const resolvingSuspension = !orgSuspended && registries === null && !!user?.accessToken;

    // Name exactly what was suspended (game + build), so the message isn't vague. The registry only
    // carries the game name (its map key) and the 8-digit client id; resolve the friendlier client
    // NAME best-effort from the game catalogue (same accessibility endpoint the Profile page uses).
    // Falls back to the raw id if the lookup fails — the message still renders either way.
    const [suspendedScopes, setSuspendedScopes] = useState([]); // [{ gameName, state, clients: [{id, name}] }]
    const suspendedScopesKey = useMemo(() => (
        membershipSuspended
            ? suspendedScopeDetails(registries)
                .map(s => `${s.orgId}:${s.gameKey}:${s.clientIds.join('|')}`).join(',')
            : ''
    ), [membershipSuspended, registries]);
    useEffect(() => {
        if (!membershipSuspended || !user?.accessToken) { setSuspendedScopes([]); return; }
        const scopes = suspendedScopeDetails(registries);
        if (!scopes.length) { setSuspendedScopes([]); return; }
        let cancelled = false;
        const orgIds = [...new Set(scopes.map(s => s.orgId).filter(Boolean))];
        Promise.allSettled(orgIds.map(orgId =>
            getGameRegistriesByOrgIdWithAccessibility({ accessToken: user.accessToken, orgId })
                .then(res => [orgId, res?.games || []])
        )).then(results => {
            if (cancelled) return;
            // clientId → clientName, gameKey(name or id) → gameName, across the resolved catalogues.
            const clientNameById = {};
            const gameNameByKey = {};
            results.forEach(r => {
                if (r.status !== 'fulfilled') return;
                const [, games] = r.value;
                (games || []).map(e => e?.game || e).filter(Boolean).forEach(g => {
                    if (g.id) gameNameByKey[g.id] = g.gameName || g.id;
                    if (g.gameName) gameNameByKey[g.gameName] = g.gameName;
                    (g.builds || []).forEach(b => {
                        if (b?.gameClientId) clientNameById[String(b.gameClientId)] = b.clientName || '';
                    });
                });
            });
            setSuspendedScopes(scopes.map(s => ({
                gameName: gameNameByKey[s.gameKey] || s.gameKey,
                state: s.state,
                clients: (s.clientIds || []).map(id => ({ id: String(id), name: clientNameById[String(id)] || '' })),
            })));
        });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [suspendedScopesKey, user?.accessToken]);

    // Synchronous fallback (ids only, no friendly names) so the detailed message renders the instant
    // registries load — the async name lookup above then enriches it in place (no flash to a vaguer
    // copy, and it still works if the catalogue lookup is slow or fails).
    const baseScopes = useMemo(() => (
        membershipSuspended
            ? suspendedScopeDetails(registries).map(s => ({
                gameName: s.gameKey,
                state: s.state,
                clients: (s.clientIds || []).map(id => ({ id: String(id), name: '' })),
            }))
            : []
    ), [membershipSuspended, registries]);
    const resolvedScopes = suspendedScopes.length ? suspendedScopes : baseScopes;

    // A single suspended build (the reported case) reads differently from several — call it out so
    // the user understands one more accessible build/game would let them in.
    const totalSuspendedClients = resolvedScopes.reduce((n, s) => n + (s.clients?.length || 0), 0);
    const singleBuildLockout = membershipSuspended && resolvedScopes.length === 1 && totalSuspendedClients === 1;

    // Which state locked them out — the card's word, icon and advice all follow it. A MIXED set
    // (some scopes suspended, others archived) can't be produced by Game User Management, so it only
    // arises from legacy data; it reads "suspended or archived" and the list below names each scope.
    const scopeStates = [...new Set(resolvedScopes.map(s => s.state).filter(Boolean))];
    const lockState = orgDenyingState
        || (scopeStates.length === 1 ? scopeStates[0] : scopeStates.length ? 'MIXED' : null);
    const archived = String(lockState).toUpperCase() === 'ARCHIVED';
    const stateVerb = archived
        ? 'archived'
        : lockState === 'MIXED' ? 'suspended or archived' : 'suspended';
    const advice = ADVICE[orgSuspended ? 'org' : singleBuildLockout ? 'singleBuild' : 'scopes'][archived ? 'ARCHIVED' : 'SUSPENDED'];

    // Suspension is per-org: if the user belongs to other orgs that are NOT suspended, offer to
    // switch there rather than leaving them stuck on a dead-end page.
    const activeOrgs = orgs.filter(o =>
        o.orgId && o.orgId !== currentOrg?.orgId && !DENYING_STATES.includes(o.orgState)
    );

    // userDetails.orgs carries only { orgId, orgState } — no name. Fetch the names of EVERY org the
    // user belongs to, so both the suspended org named in the message and the switchable orgs in the
    // dropdown read by name instead of a generic label.
    const [orgNamesById, setOrgNamesById] = useState({});
    // The org picked in the dropdown — switching is deferred until the user confirms via the button,
    // so a stray selection doesn't immediately reload them somewhere.
    const [switchTargetId, setSwitchTargetId] = useState('');
    const namedIdsKey = orgs.map(o => o.orgId).filter(Boolean).sort().join(',');
    useEffect(() => {
        const ids = namedIdsKey ? namedIdsKey.split(',') : [];
        if (!ids.length || !user?.accessToken) return;
        let cancelled = false;
        Promise.allSettled(ids.map(id => getOrgById({ accessToken: user.accessToken, id })))
            .then(results => {
                if (cancelled) return;
                const names = {};
                results.forEach((r, i) => {
                    const data = r.status === 'fulfilled' ? r.value : null;
                    // getOrgById resolves to { org: { orgName, ... } } (same shape DashboardHeader reads);
                    // tolerate a couple of alternates just in case.
                    const name = data?.org?.orgName || data?.orgRegistryDTO?.orgName || data?.orgName;
                    if (name) names[ids[i]] = name;
                });
                setOrgNamesById(names);
            });
        return () => { cancelled = true; };
    }, [namedIdsKey, user?.accessToken]);

    const orgLabelFor = (org) => orgNamesById[org.orgId] || org.orgName || 'your other organization';

    const switchOrg = (org) => {
        // Point the (redux-persisted) context at the new org.
        dispatch(updateDashboardContext({
            selectedOrgId: org.orgId,
            selectedOrgName: orgNamesById[org.orgId] || org.orgName || null,
            selectedGameId: null,
            selectedGameName: null,
            selectedGameClientId: null,
        }));
        // Clear the suspended org's cached permissions so route guards show their loading state
        // (usePermission → null) instead of acting on the old org's NO_ACCESS — without this a
        // plain navigate would bounce straight back to /not-authorized. The dashboard layout then
        // fetches the new org's permissions on mount. SPA navigate (no full reload) = no flash.
        dispatch(setEffectivePermissions(null));
        // Persist the active context server-side (fire-and-forget; redux already drives the UI).
        updateActiveContext({
            accessToken: user?.accessToken,
            activeOrgId: org.orgId,
            activeGameId: null,
            activeClientId: null,
        }).catch(e => console.error('Failed to persist active context on org switch:', e));
        // The signed-in landing, not /home (which is the public marketing landing).
        navigate(LANDING_PATH);
    };

    // /not-authorized is a PUBLIC route, so it is perfectly reachable with no session at all —
    // pasting a deep link while signed out lands here. Offering "Sign out" to someone who is
    // already signed out is both useless and confusing; they need the way in, not the way out.
    // Same test PublicHeader uses, so the two agree on what "signed in" means.
    const isSignedIn = !!user?.accessToken;

    const handleSignOut = async () => {
        try {
            await logOut();
            // logOut only clears auth state; it doesn't navigate. /not-authorized is a public
            // route so nothing redirects us off it — push to /home and the route guard sends the
            // now-signed-out user to the login page (mirrors PublicHeader's sign-out).
            navigate('/home');
        } catch (error) {
            console.error('Sign out failed:', error);
        }
    };

    // Brief hold while we determine whether a (non-org) lockout is a suspension or a plain
    // permission gap — avoids flashing the generic "Not Authorized" card then swapping it.
    if (resolvingSuspension) {
        return (
            <div className="error-page">
                <div className="error-card">
                    <p className="error-subtle">Checking your access…</p>
                </div>
            </div>
        );
    }

    if (suspended) {
        const orgLabel = orgNamesById[currentOrg?.orgId] || currentOrg?.orgName;
        return (
            <div className="error-page">
                <div className="error-card">
                    <div className={`error-icon error-icon--${archived ? 'archived' : 'suspended'}`} aria-hidden="true">
                        <FontAwesomeIcon icon={archived ? faBoxArchive : faCirclePause} />
                    </div>
                    <h1>{archived ? 'Access Archived' : 'Access Suspended'}</h1>
                    {orgSuspended ? (
                        <p>
                            Your access to {orgLabel ? <strong className="error-org-name">{orgLabel}</strong> : 'this organization'} has been {stateVerb}.
                        </p>
                    ) : singleBuildLockout ? (
                        <p>
                            Your access to{' '}
                            <strong className="error-org-name">{resolvedScopes[0].gameName}</strong>
                            {resolvedScopes[0].clients[0] && (
                                <> — {resolvedScopes[0].clients[0].name
                                    ? `${resolvedScopes[0].clients[0].name} (${resolvedScopes[0].clients[0].id})`
                                    : resolvedScopes[0].clients[0].id}</>
                            )}{' '}has been {stateVerb}.
                        </p>
                    ) : resolvedScopes.length > 0 ? (
                        <p>Your access to the following has been {stateVerb}:</p>
                    ) : (
                        <p>Your game access has been {stateVerb}.</p>
                    )}

                    {/* Itemize when more than one game/build is suspended. */}
                    {!orgSuspended && !singleBuildLockout && resolvedScopes.length > 0 && (
                        <ul className="error-suspended-list">
                            {resolvedScopes.map((s) => (
                                <li key={s.gameName}>
                                    <strong className="error-org-name">{s.gameName}</strong>
                                    {s.clients.length > 0 && (
                                        <> — {s.clients.map(c => c.name ? `${c.name} (${c.id})` : c.id).join(', ')}</>
                                    )}
                                </li>
                            ))}
                        </ul>
                    )}

                    <p className="error-subtle">{advice}</p>
                    <div className="error-actions">
                        {activeOrgs.length > 0 && (
                            <div className="error-switch-select">
                                <Select
                                    label="Switch to another organization"
                                    name="switch-org"
                                    placeholder="Select an organization"
                                    value={switchTargetId}
                                    options={[
                                        { value: '', label: 'Select an organization' },
                                        ...activeOrgs.map(o => ({ value: o.orgId, label: orgLabelFor(o) })),
                                    ]}
                                    onChange={(e) => setSwitchTargetId(e.target.value)}
                                    fullWidth
                                />
                                <PrimaryButton
                                    disabled={!switchTargetId}
                                    onClick={() => {
                                        const org = activeOrgs.find(o => o.orgId === switchTargetId);
                                        if (org) switchOrg(org);
                                    }}
                                >
                                    Go to Dashboard
                                </PrimaryButton>
                            </div>
                        )}
                        {isSignedIn
                            ? <SecondaryButton onClick={handleSignOut}>Sign Out</SecondaryButton>
                            : <SecondaryButton onClick={() => navigate('/account?mode=login')}>Sign In</SecondaryButton>}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="error-page">
            <div className="error-card">
                <div className="error-icon" aria-hidden="true">
                    <FontAwesomeIcon icon={faLock} />
                </div>
                <h1>Not Authorized</h1>
                {isSignedIn ? (
                    <p>You don't have permission to access this page.</p>
                ) : (
                    <p>You're not signed in. Sign in to check whether you have access to this page.</p>
                )}
                <div className="error-actions">
                    {isSignedIn ? (
                        <>
                            <PrimaryButton onClick={() => navigate('/home')}>Go to Homepage</PrimaryButton>
                            <SecondaryButton onClick={handleSignOut}>Sign Out</SecondaryButton>
                        </>
                    ) : (
                        <>
                            <PrimaryButton onClick={() => navigate('/account?mode=login')}>Sign In</PrimaryButton>
                            <SecondaryButton onClick={() => navigate('/home')}>Go to Homepage</SecondaryButton>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default NotAuthorized;
